import type { ReactNode } from 'react';
import type { Permission, UserRole } from '@genoma/shared';
import { NavLink } from 'react-router-dom';

import logo from '../assets/leadership-accelerator-logo.png';
import { useI18n } from '../i18n/index.tsx';
import { useAuthStore } from '../stores/auth.store.ts';
import { useCycleStore } from '../stores/cycle.store.ts';
import { CyclePicker } from './CyclePicker.tsx';
import { CycleSwitch } from './CycleSwitch.tsx';
import { LanguageSwitch } from './LanguageSwitch.tsx';

/**
 * The frame around every signed-in screen.
 *
 * A dark rail on the left, from the approved mockup. It holds one section for
 * now; the rest of the programme — evaluations, reports, sessions, insights —
 * arrives as those screens are built, and each gets a line here rather than a
 * new place to look.
 */

type NavItem = {
  to: string;
  labelKey: string;
  icon: ReactNode;
  /** Absent means everyone signed in sees it. */
  permission?: Permission;
  /**
   * A role the person must actually hold, on top of the permission.
   *
   * For the one screen that is about the reader rather than about the cycle.
   * An admin holds every permission, `report:read:own` included, so a
   * permission alone put "Mi reporte" in the rail of people who are not
   * evaluated and have no report to read.
   */
  role?: UserRole;
};

const HomeIcon = (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden>
    <path d="M3 9.5 12 3l9 6.5V21H3z" />
    <path d="M9 21v-7h6v7" />
  </svg>
);

const PeopleIcon = (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  </svg>
);

const SendIcon = (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" aria-hidden>
    <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" stroke="currentColor" strokeLinejoin="round" />
  </svg>
);

const CoachIcon = (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" aria-hidden>
    <circle cx="12" cy="8" r="3.4" stroke="currentColor" />
    <path
      d="M5 20a7 7 0 0 1 14 0"
      stroke="currentColor"
      strokeLinecap="round"
    />
  </svg>
);

const ReportIcon = (
  <svg viewBox="0 0 24 24" fill="none" strokeWidth="1.6" aria-hidden>
    <path d="M6 2h8l4 4v16H6z" stroke="currentColor" strokeLinejoin="round" />
    <path d="M14 2v4h4M9 12h6M9 16h6" stroke="currentColor" strokeLinecap="round" />
  </svg>
);

const NAV: NavItem[] = [
  { to: '/panel', labelKey: 'nav.overview', icon: HomeIcon, permission: 'progress:read' },
  // A leader's only screen, and it means nothing to anybody else: "Mi reporte"
  // in the rail of somebody who is not being evaluated points at a report that
  // does not exist. Being evaluated is what PARTICIPANT records, so that — not
  // the permission — is what puts this line in the rail.
  {
    to: '/mi-reporte',
    labelKey: 'nav.myReport',
    icon: ReportIcon,
    permission: 'report:read:own',
    role: 'PARTICIPANT',
  },
  {
    to: '/participantes',
    labelKey: 'nav.participants',
    icon: PeopleIcon,
    permission: 'progress:read',
  },
  // Everyone doing the assessing — three quarters of the people in a cycle,
  // and where the chasing actually happens.
  { to: '/evaluadores', labelKey: 'nav.raters', icon: SendIcon, permission: 'rater:read' },
  // Who coaches whom. Admin only, because an assignment is the whole of what
  // a coach can see.
  // P&O read this one; only an admin can change anything on it.
  { to: '/coaches', labelKey: 'nav.coaches', icon: CoachIcon, permission: 'coach:read' },
  // The client's own P&O accounts. Admin only.
  { to: '/equipo-po', labelKey: 'nav.staff', icon: CoachIcon, permission: 'staff:manage' },
];

export const AppShell = ({ children }: { children: ReactNode }) => {
  const { t } = useI18n();
  const user = useAuthStore((state) => state.user);
  const can = useAuthStore((state) => state.can);
  const signOut = useAuthStore((state) => state.signOut);
  // Every panel screen fetches on mount, so changing cycle remounts them
  // rather than teaching each one to listen.
  // Keyed 'default' until somebody picks another, so the list arriving does
  // not remount a screen that is already showing the right cycle.
  const cycleKey = useCycleStore(
    (state) =>
      state.cycles.find((cycle) => cycle.id === state.selectedId && !cycle.isDefault)?.id ??
      'default',
  );
  // A link to a screen that answers 403 is worse than no link.
  const nav = NAV.filter(
    (item) =>
      (!item.permission || can(item.permission)) &&
      (!item.role || (user?.roles.includes(item.role) ?? false)),
  );

  return (
    <div className="flex min-h-full flex-col md:flex-row">
      <aside className="flex shrink-0 flex-col bg-[var(--color-rail)] px-4 py-5 text-[#e9e8e3] md:sticky md:top-0 md:h-screen md:w-60 md:py-6 print:hidden">
        {/* The programme's own mark, which already says Leadership
            Accelerator — so the line under it names the client instead.
            Somebody at BASAC is using their own organization's instrument, not
            visiting a supplier. Genoma signs it at the bottom, where a maker
            belongs: after the work, not in front of it. */}
        <div className="px-2 pb-5">
          <img
            src={logo}
            alt={user?.brand.program ?? 'Leadership Accelerator'}
            className="w-[150px] max-w-full"
          />
          <p className="mt-2 font-mono text-[9.5px] tracking-[0.13em] text-white/40 uppercase">
            {user?.brand.organization || 'BASAC'} · 360
          </p>
        </div>

        <nav className="flex flex-col gap-0.5">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-[0.92rem] font-medium transition',
                  isActive
                    ? 'bg-[rgba(74,186,191,0.14)] text-white [&_svg]:stroke-[var(--color-rail-bright)]'
                    : 'text-[var(--color-rail-text)] hover:bg-white/5 hover:text-white',
                ].join(' ')
              }
            >
              <span className="size-[18px] shrink-0 [&>svg]:size-full [&>svg]:stroke-current">
                {item.icon}
              </span>
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>

        {/* Whether anybody can answer, next to the sections whose numbers it
            explains. A closed cycle makes "why is nobody answering" the wrong
            question, and that is worth knowing without going to look for it. */}
        <CyclePicker />
        <CycleSwitch key={cycleKey} />

        {user && (
          <div className="mt-auto hidden border-t border-white/10 pt-3.5 md:block">
            <p className="px-3 text-[13px] font-semibold text-white">{user.fullName}</p>
            <p className="px-3 text-[11.5px] text-[#9c9a93]">
              {user.roles.map((role) => t(`roles.${role}`)).join(' · ')}
            </p>
            <LanguageSwitch />

            <button
              type="button"
              onClick={() => void signOut()}
              className="mt-1.5 cursor-pointer border-none bg-transparent px-3 py-1 font-mono text-[10px] tracking-[0.1em] text-[#9c9a93] uppercase transition hover:text-white"
            >
              {t('auth.signOut')}
            </button>

            {/* Last, and quieter than signing out: it is a thing to consult,
                not a thing to do. Lower case rather than the rail's mono caps,
                which are for actions — this one reads as a document. Everyone
                who runs the cycle should be able to point at it when a rater
                asks who reads what they write. */}
            <NavLink
              to="/privacidad"
              className="block px-3 py-1 text-[11px] text-[#7d7b75] transition hover:text-white"
            >
              {t('privacy.link')}
            </NavLink>
          </div>
        )}

        {/* Powered by, and that is all: the glyph at the size a credit
            deserves, well below the client's own name. */}
        <a
          href="https://wearegenoma.com"
          target="_blank"
          rel="noreferrer"
          className="mt-3 hidden items-center gap-1.5 px-3 text-white/25 transition hover:text-white/50 md:flex"
        >
          <svg viewBox="0 0 65.14 87.49" fill="currentColor" aria-hidden className="h-3 w-auto">
            <path
              fillRule="evenodd"
              d="m28.14.67C12.6.67,0,13.27,0,28.81s12.6,28.14,28.14,28.14,28.14-12.6,28.14-28.14S43.68.67,28.14.67h0Zm-15.12,28.14c0-8.35,6.77-15.12,15.12-15.12s15.12,6.77,15.12,15.12-6.77,15.12-15.12,15.12-15.12-6.77-15.12-15.12h0Z"
            />
            <path
              fillRule="evenodd"
              d="m0,59.35c0,15.54,12.6,28.14,28.14,28.14s28.14-12.6,28.14-28.14h-13.02c0,8.35-6.77,15.12-15.12,15.12s-15.12-6.77-15.12-15.12H0Z"
            />
          </svg>
          <span className="font-mono text-[9px] tracking-[0.1em] uppercase">
            powered by Genoma
          </span>
        </a>
      </aside>

      <main key={cycleKey} className="min-w-0 flex-1">
        {children}
      </main>
    </div>
  );
};
